import { defineStore } from 'pinia';
import type { AuthConfigDto, Identity, Role } from '@igd/shared';
import { api, ApiError, lireIdentiteDev, memoriserIdentiteDev } from '@/lib/api';
import { demoActive } from '@/lib/modeDemo';

interface State {
  user: Identity | null;
  config: AuthConfigDto;
  ready: boolean;
  loading: boolean;
}

/** `/me` renvoie `Identity` (contrat) ; on tolère aussi `{ user }` (gabarit de Plans d'actions). */
function extraireIdentite(data: Identity | { user: Identity | null } | null | undefined): Identity | null {
  if (!data) return null;
  return 'user' in data ? data.user : data;
}

/**
 * L'IDENTITÉ de la personne connectée, lue sur `/api/me`. Les rôles qui s'y
 * trouvent pilotent le menu et les gardes de route ; l'API, elle, les rejoue
 * à chaque appel — masquer un écran n'est qu'un confort, pas une protection.
 */
export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    user: null,
    config: { sso: false, devLogin: false },
    ready: false,
    loading: false,
  }),
  getters: {
    isAuthenticated: (s): boolean => s.user !== null,
    roles: (s): Role[] => s.user?.roles ?? [],
    hasRole:
      (s) =>
      (...roles: Role[]): boolean =>
        (s.user?.roles ?? []).some((r) => roles.includes(r)),
  },
  actions: {
    /** Restaure la session : cookie SSO, ou identité de dev mémorisée. */
    async init(): Promise<void> {
      if (this.ready) return;
      // En démo, la configuration de connexion n'a pas de sens (l'identité est
      // le rôle endossé) : on ne la lit pas, et surtout on ne touche pas à
      // l'identité de dev mémorisée, qui doit survivre à la sortie de démo.
      if (!demoActive()) {
        try {
          this.config = await api.get<AuthConfigDto>('/auth/config');
        } catch {
          // API indisponible : on reste déconnecté, la page de connexion le dira.
        }
        // Une identité de dev mémorisée alors que le mode dev est coupé (poste
        // qui a servi en local, puis en staging) ne doit pas partir en en-tête.
        if (!this.config.devLogin && lireIdentiteDev()) memoriserIdentiteDev(null);
      }
      await this.fetchMe();
      this.ready = true;
    },

    /** Relit configuration et identité : après une entrée ou une sortie du mode démo. */
    async recharger(): Promise<void> {
      this.ready = false;
      await this.init();
    },

    async fetchMe(): Promise<void> {
      try {
        this.user = extraireIdentite(await api.get<Identity | { user: Identity | null }>('/me'));
      } catch {
        // 401 attendu quand on n'est pas connecté ; toute autre panne aussi
        // renvoie à la connexion, qui affichera l'état de l'API.
        this.user = null;
      }
    },

    /** Redirige vers Microsoft ; le retour pose le cookie de session. */
    loginWithMicrosoft(redirect = '/'): void {
      window.location.href = `/api/auth/login?redirect=${encodeURIComponent(redirect)}`;
    },

    /** Connexion de développement : identification par e-mail (X-Debug-User). */
    async devLogin(email: string): Promise<void> {
      this.loading = true;
      try {
        memoriserIdentiteDev(email);
        await this.fetchMe();
        if (!this.user) {
          memoriserIdentiteDev(null);
          throw new ApiError(401, "Compte inconnu ou inactif : il n'est pas ouvert dans l'outil.");
        }
        try {
          // Journalise la connexion (journal « connexions » de l'administrateur).
          await api.post('/auth/session-start');
        } catch {
          /* non bloquant */
        }
      } finally {
        this.loading = false;
      }
    },

    async majAdresseDomicile(adresseDomicile: string | null): Promise<void> {
      this.user = extraireIdentite(await api.patch<Identity | { user: Identity }>('/me', { adresseDomicile }));
    },

    async logout(): Promise<void> {
      try {
        await api.post('/auth/logout');
      } catch {
        // Session déjà expirée : on nettoie quand même côté client.
      }
      memoriserIdentiteDev(null);
      this.user = null;
    },
  },
});
